/**
 * ResetRecoverPage — landing for both the self-service reset link
 * (/api/auth/reset/request) and the admin-triggered reset link
 * (/api/admin/passkey/user/:id/reset). Either flow wipes the user's other
 * passkeys; the email link grants 15 minutes of auth to register a new
 * passkey on this device.
 *
 * Flow:
 *   1. Read ?token_hash=… from URL.
 *   2. carmenAuth.exchangeToken({ token_hash }) → session.
 *   3. User clicks → registerPasskey() (WebAuthn needs a user gesture).
 *   4. finalizeReset() removes the other passkeys, navigate to /download.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { carmenAuth } from "@/lib/carmenAuth";
import { finalizeReset, registerPasskey } from "@/lib/passkey";

const PANEL_CLASS =
  "rounded-[32px] border border-[rgba(10,52,96,0.10)] bg-white/92 p-8 shadow-[0_24px_64px_rgba(10,52,96,0.10)] backdrop-blur-xl sm:p-10";
const PRIMARY_BUTTON_CLASS =
  "w-full rounded-full bg-[var(--cs-title-navy)] px-5 py-3.5 text-[15px] font-semibold text-white shadow-[0_16px_34px_rgba(10,52,96,0.18)] transition-colors duration-200 hover:bg-[var(--cs-title-navy)] disabled:cursor-not-allowed disabled:opacity-50";

type Phase = "verifying" | "ready" | "registering";

export function ResetRecoverPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tokenHash = params.get("token_hash") ?? "";

  const [phase, setPhase] = useState<Phase>("verifying");
  const [error, setError] = useState("");
  // The access token verifyOtp() hands back. We register with this directly
  // instead of re-reading getSession() at click-time, which can return null in
  // some browsers (e.g. email in-app webviews) and leave the call unauthed.
  const accessTokenRef = useRef<string>("");

  useEffect(() => {
    if (!tokenHash) {
      setError("Ongeldige link.");
      setPhase("ready");
      return;
    }
    let cancelled = false;
    void (async () => {
      // Use the History API directly rather than setParams(), which would
      // change `tokenHash` and re-fire this effect into the empty-token
      // branch, clobbering the verified state.
      const { session, error: exchangeError } = await carmenAuth.exchangeToken({
        token_hash: tokenHash,
      });
      if (cancelled) return;
      window.history.replaceState({}, "", "/recover/reset");
      if (exchangeError) {
        setError(
          /expired|invalid/i.test(exchangeError.message)
            ? "Deze link is verlopen of al gebruikt. Vraag een nieuwe aan via 'Passkey kwijt?' op de inlogpagina."
            : exchangeError.message,
        );
      } else {
        accessTokenRef.current = session?.access_token ?? "";
      }
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenHash]);

  const handleRegister = useCallback(async () => {
    setError("");
    setPhase("registering");
    try {
      const result = await registerPasskey("Primary device", {
        accessToken: accessTokenRef.current || undefined,
        reset: true,
      });
      await finalizeReset(result.credential_id, accessTokenRef.current || undefined);
      nav("/download", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registratie mislukt.");
      setPhase("ready");
    }
  }, [nav]);

  const canRegister = (phase === "ready" || phase === "registering") && !error;

  return (
    <div className="cs-marketing min-h-screen bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f3f9fd_100%)] px-4 py-12 sm:px-6 sm:py-16">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div className={PANEL_CLASS}>
          <div className="mb-6 text-center">
            <h1 className="text-[32px] font-semibold tracking-[-0.04em] text-[var(--cs-title-navy)]">
              Nieuwe passkey instellen
            </h1>
          </div>

          {error ? (
            <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
              {error}
            </div>
          ) : null}

          {phase === "verifying" ? (
            <p className="text-center text-[15px] text-[rgba(95,131,160,0.96)]">
              Bezig met verifiëren…
            </p>
          ) : canRegister ? (
            <>
              <p className="mb-6 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
                Registreer een nieuwe passkey op dit toestel — u wordt gevraagd om uw
                vingerafdruk, gezichts-ID of pincode. Uw oude passkeys worden daarna verwijderd en
                u wordt op andere apparaten afgemeld.
              </p>
              <button
                type="button"
                onClick={() => void handleRegister()}
                disabled={phase === "registering"}
                className={PRIMARY_BUTTON_CLASS}
              >
                {phase === "registering" ? "Bezig…" : "Passkey registreren"}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
